import { Controller, useFormContext } from 'react-hook-form';
import { Grid, FormControl, InputLabel, MenuItem, Select, InputAdornment, FormHelperText } from '@mui/material';
import type { CountryEntity } from '@shared/api';
import { Input } from '@shared/widgets';

interface Props {
  countries: CountryEntity[];
}

export const StepSupplementaryContact = (props: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Grid container>
      <Grid item xs={6}>
        <Controller
          name='residentialAddressLine1'
          control={control}
          rules={{ required: 'Residential Address is required' }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Residential Address' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='residentialAddressLine2'
          control={control}
          rules={{ required: 'Address Line 2 is required' }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Address Line 2' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='city'
          control={control}
          rules={{ required: 'City is required' }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='City' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='postcode'
          control={control}
          rules={{ required: 'Postcode is required' }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Postcode' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        {props.countries.length > 0 ? (
          <FormControl margin='normal' fullWidth error={!!errors.country}>
            <InputLabel>Country *</InputLabel>
            <Controller
              name='country'
              control={control}
              rules={{ required: 'Country is required' }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select {...field} variant='standard' fullWidth>
                    {props.countries.map((country) => (
                      <MenuItem key={country.name} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>{error?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        ) : (
          <Controller
            name='country'
            control={control}
            rules={{ required: 'Country is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input {...field} label='Country' margin='normal' required error={!!error} helperText={error?.message} />
            )}
          />
        )}
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='alternativeMailingAddressLine1'
          control={control}
          render={({ field }) => <Input {...field} label='Alternative Mailing Address' margin='normal' />}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='alternativeMailingAddressLine2'
          control={control}
          render={({ field }) => <Input {...field} label='Alternative Mailing Address Line 2' margin='normal' />}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='alternativeMailingAddressCity'
          control={control}
          render={({ field }) => <Input {...field} label='City' margin='normal' />}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='alternativeMailingAddressPostcode'
          control={control}
          render={({ field }) => <Input {...field} label='Postcode' margin='normal' />}
        />
      </Grid>
      <Grid item xs={4}>
        {props.countries.length > 0 ? (
          <FormControl margin='normal' fullWidth>
            <InputLabel>Country</InputLabel>
            <Controller
              name='alternativeMailingAddressCountry'
              control={control}
              render={({ field }) => (
                <Select {...field} variant='standard' fullWidth>
                  {props.countries.map((country) => (
                    <MenuItem key={country.name} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        ) : (
          <Controller
            name='alternativeMailingAddressCountry'
            control={control}
            render={({ field }) => <Input {...field} label='Country' margin='normal' />}
          />
        )}
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='mobileNumber'
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label='Mobile Telephone Number'
              margin='normal'
              required
              error={!!error || !!errors.mobileAreaCode}
              helperText={(!!error || !!errors.mobileAreaCode) && 'Mobile Telephone Number is required'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start' sx={{ width: '80px' }}>
                    {props.countries.length > 0 ? (
                      <Controller
                        name='mobileAreaCode'
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select {...field} variant='standard' fullWidth renderValue={(item) => `+ ${item}`}>
                            {props.countries.map((item) => (
                              <MenuItem key={item.code} value={item.code}>
                                +{item.code} {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    ) : (
                      <Controller
                        name='mobileAreaCode'
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            fullWidth
                            inputProps={{ maxLength: 3 }}
                            InputProps={{ startAdornment: <InputAdornment position='start'>+</InputAdornment> }}
                          />
                        )}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='alternativeContactNumber'
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label='Alternative Telephone Number'
              margin='normal'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start' sx={{ width: '80px' }}>
                    {props.countries.length > 0 ? (
                      <Controller
                        name='alternateAreaCode'
                        control={control}
                        render={({ field }) => (
                          <Select {...field} variant='standard' fullWidth renderValue={(item) => `+ ${item}`}>
                            {props.countries.map((item) => (
                              <MenuItem key={item.code} value={item.code}>
                                +{item.code} {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    ) : (
                      <Controller
                        name='alternateAreaCode'
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            fullWidth
                            inputProps={{ maxLength: 3 }}
                            InputProps={{ startAdornment: <InputAdornment position='start'>+</InputAdornment> }}
                          />
                        )}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='email'
          control={control}
          rules={{ required: 'Title is required' }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Email' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
    </Grid>
  );
};
